// scheduling/components/reports/TerminatedReport.jsx
// Terminated Employee view (Trello #249).
//
// DJB: "Not a rush, just for the list. I need a view that only I can see where it
// shows any jobs scheduled on terminated employees."
//
// An EXCEPTION list, not a data table. The desired reading is zero rows, and on prod
// today it is one, so the empty state has to say "nothing to clean up" rather than look
// like a broken query. Everything about the layout follows from that: a headline count,
// a short row list, and a way out to the fix.
//
// --- WHY EACH ROW LINKS INTO THE GRID --------------------------------------
// The grid already renders terminated-but-scheduled staff (schedData tags them
// ScheduledInactive with an INACTIVE marker so their blocks do not vanish the moment
// peSync flips IsActive=0) and reassigning from there already works. What the grid
// cannot do is get you to them: it only draws a row when the visible week window holds
// the entries, and today's one case sits in Feb-Mar 2027, about 24 weeks out. Nobody
// scrolls there.
//
// So the report's whole job is discovery, and each row hands off with the staff, job,
// AND week pre-set. ?week= accepts any date inside the target week (weekParamToOffset
// calls getMonday on it), so the first entry date passes through as-is. That is also
// why this report needs no write action of its own and Reporting stays read-only.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchTerminatedStaff } from '../../services/reportsApi';
import { useSort, SortableTh, PlainTh } from './SortableTh';
import { exportCsv } from '../../utils/csv';

// Every datetime in this database defaults to getutcdate(), and BMSS is Central. That
// applies to DATE-only columns too, which is the non-obvious part: 2027-02-22T00:00Z
// read in Central is 2027-02-21. The server hands these over as YYYY-MM-DD strings for
// exactly that reason, so they render verbatim -- no Date parsing, which would
// reintroduce the shift (new Date('2027-02-22') parses as UTC).
const fmtDate = (iso) => (iso || '—');

const SORT_SPECS = {
  hours: { get: (r) => r.futureHours, dir: 'desc' },
  staff: { get: (r) => r.staffName, dir: 'asc' },
  level: { get: (r) => r.level, dir: 'asc' },
  dept: { get: (r) => r.dept, dir: 'asc' },
  ended: { get: (r) => r.endDate, dir: 'desc' },
  client: { get: (r) => r.clientName, dir: 'asc' },
  job: { get: (r) => r.jobName, dir: 'asc' },
  entries: { get: (r) => r.entries, dir: 'desc' },
  first: { get: (r) => r.firstEntry, dir: 'asc' },
  last: { get: (r) => r.lastEntry, dir: 'desc' },
};

const CSV_COLUMNS = [
  { label: 'Staff', get: (r) => r.staffName },
  { label: 'Level', get: (r) => r.level },
  { label: 'Dept', get: (r) => r.dept },
  { label: 'Office', get: (r) => r.office },
  { label: 'End date', get: (r) => r.endDate },
  { label: 'Client', get: (r) => r.clientName },
  { label: 'Job', get: (r) => r.jobName },
  { label: 'Job code', get: (r) => r.jobCode },
  { label: 'Service', get: (r) => r.serviceIndex },
  { label: 'Scheduled hours', get: (r) => r.futureHours },
  { label: 'Entries', get: (r) => r.entries },
  { label: 'First entry', get: (r) => r.firstEntry },
  { label: 'Last entry', get: (r) => r.lastEntry },
  { label: 'Job deadline', get: (r) => r.deadline },
];

// Caller's local date, not the server's. The Function runs UTC, so after ~7pm Central
// its "today" is already tomorrow and a block scheduled for today would drop out.
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function TerminatedReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const sort = useSort(SORT_SPECS, 'hours', 'tsort');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTerminatedStaff({ today: todayISO() })
      .then((r) => { if (!cancelled) { setData(r); setError(null); setLoading(false); } })
      .catch((e) => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => sort.sortRows(data?.rows || []), [data, sort]);
  const summary = data?.summary;

  // Open the orphaned block in the grid.
  //
  // This deeplink has to NAME every filter that could hide the row, because
  // useUrlState falls back to sessionStorage (rprs_grid_state) for any param that is
  // ABSENT -- so whatever filters the user last had on the grid are still in force.
  // The one that actually bit: "My Schedule" is deptFilter=['mine'], which restricts
  // the staff list to ONLY the current user, so landing on someone else showed an
  // empty grid. dept=all clears it. An explicit empty value (q=, cq=, client=) does
  // clear a param -- only a missing one falls through to the saved state.
  //
  // Deliberately does NOT pass ?job=. That param means "focus this job", and the grid
  // responds by force-switching to Jobs view and injecting the job past the filters
  // ("focus != filter"). Both of those work against us here: this report is
  // staff-centric, and the injection evaporates the moment you click the block
  // (the handler clears ?job= so Back works), which emptied the grid mid-interaction.
  const openInGrid = (r) => {
    const params = new URLSearchParams({
      view: 'people',           // staff-centric report -> staff-centric view
      staff: String(r.staffId),
      week: r.firstEntry || todayISO(),   // usually months out; the whole point
      dept: 'all',              // clears 'mine' / a dept scope that would hide them
      office: 'all',            // clears an office scope
      hideLeadership: '0',      // a departed Member/SM must still render
      freeOnly: '0',            // keeps only staff with NO blocks -- always hides them
      q: '',                    // staff name search
      cq: '',                   // client name/code search
      client: '',               // client group
    });
    navigate(`/schedule/grid?${params.toString()}`);
  };

  const clean = summary && summary.staffCount === 0;
  const statBg = clean ? '#F0FDF4' : '#FEF2F2';
  const statFg = clean ? '#15803D' : '#B91C1C';

  return (
    <div className="sched-rpt-root">
      <div className="sched-rpt-builder">
        <div className="sched-rpt-brow sched-rpt-brow--filters">
          <span className="sched-rpt-muted-sm">
            Staff whose PE record is inactive but who still hold scheduled hours from today
            onward. Terminated means <strong>IsActive = 0</strong>, not a past end date &mdash; a
            past end date with a future start date is a returning intern, and their hours are
            legitimate.
          </span>
          {rows.length > 0 && (
            <button className="sched-rpt-csv" style={{ marginLeft: 'auto' }}
              onClick={() => exportCsv('rprs-terminated-scheduled', rows, CSV_COLUMNS)}
              title="Download these rows as CSV (UTF-8 with BOM, opens cleanly in Excel).">
              CSV
            </button>
          )}
        </div>
      </div>

      {error && <div className="sched-rpt-error">Couldn&rsquo;t load: {error}</div>}
      {loading && <div className="sched-rpt-muted">Loading&hellip;</div>}

      {!loading && !error && data && (
        <>
          <div className="sched-rpt-summary">
            <div className="sched-rpt-statgroup">
              <div className="sched-rpt-statgroup-l"
                title="Scheduled hours from today onward belonging to staff who no longer work here. Zero is the goal.">
                Orphaned scheduled work
              </div>
              <div className="sched-rpt-statrow">
                <div className="sched-rpt-stat" style={{ background: statBg }}
                  title="Distinct terminated staff, not rows — one person holding four jobs is one person to chase.">
                  <div className="sched-rpt-stat-n" style={{ color: statFg }}>{summary.staffCount}</div>
                  <div className="sched-rpt-stat-l">Staff</div>
                </div>
                <div className="sched-rpt-stat" style={{ background: statBg }}
                  title="Staff × job combinations still sitting on the schedule.">
                  <div className="sched-rpt-stat-n" style={{ color: statFg }}>{summary.jobCount}</div>
                  <div className="sched-rpt-stat-l">Jobs</div>
                </div>
                <div className="sched-rpt-stat" style={{ background: statBg }}
                  title="Total hours nobody is going to work unless they are reassigned.">
                  <div className="sched-rpt-stat-n" style={{ color: statFg }}>{summary.totalHours}</div>
                  <div className="sched-rpt-stat-l">Hours</div>
                </div>
              </div>
            </div>
            <div className="sched-rpt-legend">
              <div className="sched-rpt-muted-sm">
                Click a row to open it in the grid with the staffer, job and <em>week</em> pre-set.
                Reassign or delete the block from there &mdash; the grid keeps showing inactive staff
                who still hold work, marked INACTIVE.
              </div>
              <div className="sched-rpt-muted-sm">
                Past entries are excluded on purpose: the work either happened or it did not, and
                either way it cannot be reassigned now.
              </div>
            </div>
          </div>

          {/* Own scroll container: position:sticky needs a height-CONSTRAINED scrolling
              ancestor, and the page-level scroller has none. See .sched-rpt-scroll. */}
          <div className="sched-mgr-table-wrap sched-rpt-scroll">
            <table className="sched-mgr-table sched-rpt-table">
              <thead>
                <tr className="sched-rpt-colrow">
                  <SortableTh sort={sort} sortKey="staff" label="Staff"
                    title="Staff whose PE record is inactive (IsActive = 0). Click the row to open the block in the grid." />
                  <SortableTh sort={sort} sortKey="level" label="Level"
                    title="PE staff level as of when their record went inactive." />
                  <SortableTh sort={sort} sortKey="dept" label="Dept" title="PE department code." />
                  <SortableTh sort={sort} sortKey="ended" label="Ended"
                    title="rprs.Staff.EndDate. Em-dash when PE never recorded one — 67 of 769 inactive staff have no end date, which is exactly why this column cannot be the definition of terminated." />
                  <SortableTh sort={sort} sortKey="client" label="Client"
                    title="Client on the job. Blank for a custom job, which has no client." />
                  <SortableTh sort={sort} sortKey="job" label="Job"
                    title="Client job or custom job the hours sit on. ✦ marks a custom job." />
                  <SortableTh sort={sort} sortKey="hours" label="Hours" className="sched-mgr-th-right"
                    title="Scheduled hours from today onward on this staff/job pair." />
                  <SortableTh sort={sort} sortKey="entries" label="Entries" className="sched-mgr-th-right"
                    title="Number of individual day entries making up those hours." />
                  <SortableTh sort={sort} sortKey="first" label="First"
                    title="Earliest scheduled day from today onward — this is the week the deeplink opens." />
                  <SortableTh sort={sort} sortKey="last" label="Last" title="Latest scheduled day." />
                  <PlainTh label="" title="Open this block in the grid." />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={`${r.staffId}-${r.jobId ?? `c${r.customJobId}`}`}
                    onClick={() => openInGrid(r)} style={{ cursor: 'pointer' }}>
                    <td>{r.staffName}</td>
                    <td className="sched-rpt-muted-sm">{r.level}</td>
                    <td className="sched-rpt-muted-sm">{r.dept}</td>
                    <td className="sched-rpt-muted-sm">{fmtDate(r.endDate)}</td>
                    <td>{r.clientName || '—'}</td>
                    <td>
                      {r.jobName || '—'}
                      {r.customJobId != null && (
                        <span className="sched-rpt-muted-sm" title="Custom (non-client) job."> &#10022;</span>
                      )}
                    </td>
                    <td className="sched-mgr-td-right sched-rpt-pct">{r.futureHours}</td>
                    <td className="sched-mgr-td-right sched-rpt-muted-sm">{r.entries}</td>
                    <td className="sched-rpt-muted-sm">{fmtDate(r.firstEntry)}</td>
                    <td className="sched-rpt-muted-sm">{fmtDate(r.lastEntry)}</td>
                    <td className="sched-rpt-muted-sm">Open in grid &#8599;</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={11} className="sched-rpt-muted">
                      No terminated staff have scheduled hours. Nothing to clean up.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
